/**
 * §11.1/§11.2 single live-region contract.
 *
 * The two permanent nodes (`ls-live-polite` / `ls-live-assertive`) are
 * mounted once in the app shell, `app/layout.tsx`, right before
 * `</body>`. `lib/announce.ts` is the only module permitted to write them:
 * every player/filter/form announcement site-wide goes through
 * `announce()` rather than a component mounting its own `aria-live` node.
 *
 * `ensureNode()` below still creates the node on demand if it can't find
 * one already mounted (e.g. a unit test that renders in isolation without
 * the real layout, or a future page that somehow renders before the shell
 * does): defensive fallback only, not the primary mounting path anymore.
 */

export type AnnounceChannel = "polite" | "assertive";

const POLITE_ID = "ls-live-polite";
const ASSERTIVE_ID = "ls-live-assertive";

let lastAt = 0;
let lastMsg = "";

function ensureNode(id: string, channel: AnnounceChannel): HTMLElement | null {
  if (typeof document === "undefined") return null;
  let node = document.getElementById(id);
  if (!node) {
    node = document.createElement("div");
    node.id = id;
    node.setAttribute("role", channel === "assertive" ? "alert" : "status");
    node.setAttribute("aria-live", channel);
    node.setAttribute("aria-atomic", "true");
    node.className = "sr-only";
    document.body.appendChild(node);
  }
  return node;
}

/**
 * Announce `msg` on the given channel. Repeats of the same message within
 * 1s are suppressed (nothing on the site is time-critical, so nothing needs
 * to interrupt or repeat). `throttleMs` additionally rate-limits distinct
 * messages on the same channel (used for high-frequency events like "N
 * sounds shown").
 */
export function announce(msg: string, channel: AnnounceChannel = "polite", throttleMs = 0): void {
  if (typeof window === "undefined") return;
  const now = performance.now();
  if (msg === lastMsg && now - lastAt < 1000) return;
  if (throttleMs && now - lastAt < throttleMs) return;
  lastMsg = msg;
  lastAt = now;

  const node = ensureNode(channel === "assertive" ? ASSERTIVE_ID : POLITE_ID, channel);
  if (!node) return;
  node.textContent = "";
  requestAnimationFrame(() => {
    node.textContent = msg;
  });
}

/** mm:ss.cc formatting shared by the player's announcements and readout. */
export function mmss(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = seconds - m * 60;
  return `${m}:${s.toFixed(2).padStart(5, "0")}`;
}

/**
 * §11.2's exact player announcement strings, kept in one place so every
 * player surface (full player, mobile player, mini player) says the same
 * thing rather than reinventing copy per component.
 */
export const playerAnnouncements = {
  started: (name: string) => `Playing ${name}.`,
  paused: (atSeconds: number) => `Paused at ${atSeconds.toFixed(2)} seconds.`,
  ended: () => "Ended.",
  interrupted: (stoppedName: string, startedName: string) =>
    `Stopped ${stoppedName}. Playing ${startedName}.`,
  seeked: (currentSeconds: number, durationSeconds: number) =>
    `${currentSeconds.toFixed(2)} seconds of ${durationSeconds.toFixed(2)}.`,
  previewFailed: (reason: string) => `Preview unavailable: ${reason}. Download still works.`,
  autoplayBlocked: () => "Press play to start audio.",
} as const;
